import { useCallback, useEffect, useMemo, useState } from 'react'
import { getVisionScreens, putSensitiveMarks } from './visionApi'

/**
 * Story 213 v2 — the "Sensitive screens" block of Settings (DN-3, C6): the screens of the catalog with their
 * mark. Asks Sun only when `enabled` (Settings › Camera control, with SETTINGS). `save(otherMarks)`
 * sends one batch: the screens that changed and, after them, the other marks given (the single items of the page,
 * H-C15-1); it resolves to whether they are saved. On a rejection the changes stay pending so the user can retry (S139).
 */
const useSensitiveScreens = (enabled) => {
  const [stored, setStored] = useState([])
  const [edited, setEdited] = useState({})
  const [saveError, setSaveError] = useState('')

  useEffect(() => {
    if (!enabled) {
      return
    }
    let cancelled = false
    getVisionScreens()
      .then((screens) => {
        if (!cancelled) {
          setStored(Array.isArray(screens) ? screens : [])
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [enabled])

  const screens = useMemo(
    () => stored.map((screen) => ({ ...screen, sensitive: screen.key in edited ? edited[screen.key] : screen.sensitive })),
    [stored, edited]
  )

  const changes = useMemo(
    () => stored
      .filter((screen) => screen.key in edited && edited[screen.key] !== screen.sensitive)
      .map((screen) => ({ type: 'SCREEN', key: screen.key, sensitive: edited[screen.key] })),
    [stored, edited]
  )

  const setSensitive = useCallback((key, sensitive) => {
    setEdited((previous) => ({ ...previous, [key]: sensitive }))
  }, [])

  const save = useCallback(async (otherMarks = []) => {
    if (changes.length === 0 && otherMarks.length === 0) {
      return true
    }
    try {
      await putSensitiveMarks([...changes, ...otherMarks])
      setStored((previous) => previous.map((screen) => {
        const change = changes.find((candidate) => candidate.key === screen.key)
        return change ? { ...screen, sensitive: change.sensitive } : screen
      }))
      setEdited({})
      return true
    } catch (error) {
      setSaveError((error && error.message) || '¡Ups, something has gone wrong!')
      return false
    }
  }, [changes])

  const clearSaveError = useCallback(() => setSaveError(''), [])

  // Story 213 (RN101, S270): pending only while some screen differs from its stored mark; Discard drops them all.
  const discard = useCallback(() => setEdited({}), [])

  return { screens, setSensitive, save, saveError, clearSaveError, hasChanges: changes.length > 0, discard }
}

export default useSensitiveScreens
